import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Badge, Flex, HStack, Tooltip, Switch,
  Input, Textarea, Select, IconButton, VStack, FormLabel, useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiLink, FiClock, FiX, FiCheck } from 'react-icons/fi';
import api from '../api';
import useMenuPosition from '../hooks/useMenuPosition';

// ── Helpers ───────────────────────────────────────────────────────────────────
function safeDate(d) {
  if (!d) return null;
  // Already a Date object
  if (d instanceof Date) return isNaN(d) ? null : d;
  // Numeric timestamp
  if (typeof d === 'number') return new Date(d);
  // String: strip time component and parse YYYY-MM-DD
  const s = String(d).split('T')[0];
  const parts = s.split('-').map(Number);
  if (parts.length < 3 || !parts[0]) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}
function daysBetween(d1, d2) { return Math.round((d2 - d1) / 86400000); }
function toYMD(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : safeDate(d);
  if (!dt || isNaN(dt)) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = safeDate(d);
  return dt ? dt.toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '—';
}
function fmtDateTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }); }
  catch(e) { return s; }
}

// Traffic-light status for a bar
function barStatus(item, today) {
  const pct = item.latest_percentage || item.completion_percentage || 0;
  if (item.is_complete || item.is_completed || pct >= 100)
    return { bg: '#C6F6D5', border: '#38A169', text: '#1C4532', label: 'Complete' };
  const end = safeDate(item.planned_end || item.deadline_date || item.computed_deadline);
  const start = safeDate(item.planned_start || item.start_date || item.computed_start);
  const hasClock = (item.work_sessions || []).length > 0;
  if (end && end < today && !hasClock && pct === 0)
    return { bg: '#FED7D7', border: '#E53E3E', text: '#63171B', label: 'Overdue' };
  if (pct > 0 || hasClock)
    return { bg: '#FEFCBF', border: '#D69E2E', text: '#5F370E', label: 'In Progress' };
  if (start && start > today)
    return { bg: '#BEE3F8', border: '#3182CE', text: '#1A365D', label: 'Not Started' };
  return { bg: '#BEE3F8', border: '#3182CE', text: '#1A365D', label: 'Not Started' };
}

function getBarProps(item, minDate, dayWidth) {
  const startStr = item.computed_start || item.start_date || item.planned_start;
  const endStr   = item.computed_deadline || item.deadline_date || item.planned_end;
  const start = startStr ? safeDate(startStr) : new Date(minDate);
  const end   = endStr   ? safeDate(endStr)   : new Date(start.getTime() + 7*86400000);
  if (!start || !end || isNaN(start) || isNaN(end)) return { left:0, width:dayWidth };
  const left  = daysBetween(minDate, start) * dayWidth;
  const width = Math.max(dayWidth, daysBetween(start, end) * dayWidth);
  return { left, width, start, end };
}

const FTE_TIP = `What FTE Means
FTE expresses workload in terms of full-time staff capacity, rather than raw hours.
• 1.0 FTE = one full-time person (typically ~37.5–40 hours/week in the UK)
• 0.5 FTE = half of a full-time person
• 2.0 FTE = two full-time people`;

const ROW_H  = 44;
const BAR_T  = 8;
const BAR_H  = 28;
const HEAD_H = 48;
const LEFT_W = 268;
const RESIZE_ZONE = 8; // px from edge to show resize cursor

// ── Edit modal ────────────────────────────────────────────────────────────────
function EditModal({ item, itemType, projectId, onSave, onClose }) {
  const [form, setForm] = useState({
    task_title: item.task_title || item.title || '',
    start_date: toYMD(item.planned_start || item.start_date || ''),
    deadline_date: toYMD(item.planned_end || item.deadline_date || ''),
    expected_fte: item.expected_fte || '',
  });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        planned_start: form.start_date || null,
        planned_end: form.deadline_date || null,
        start_date: form.start_date || null,
        deadline_date: form.deadline_date || null,
        expected_fte: form.expected_fte ? parseFloat(form.expected_fte) : null,
      };
      if (itemType === 'task') {
        if (form.task_title) payload.task_title = form.task_title;
        await api.put(`/tasks/${item.id}`, payload);
      } else {
        if (form.task_title) payload.title = form.task_title;
        await api.put(`/sub-tasks/${item.id}`, payload);
      }
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      onSave();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <Modal isOpen onClose={onClose} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={1}>Edit {itemType === 'task' ? 'Task' : 'Sub-task'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <Box w="100%">
              <FormLabel fontSize="xs" mb={0.5}>Title</FormLabel>
              <Input size="sm" value={form.task_title} onChange={set('task_title')} />
            </Box>
            <HStack w="100%" spacing={2}>
              <Box flex={1}>
                <FormLabel fontSize="xs" mb={0.5}>Start Date</FormLabel>
                <Input size="sm" type="date" value={form.start_date} onChange={set('start_date')} />
              </Box>
              <Box flex={1}>
                <FormLabel fontSize="xs" mb={0.5}>End Date</FormLabel>
                <Input size="sm" type="date" value={form.deadline_date} onChange={set('deadline_date')} />
              </Box>
            </HStack>
            <Box w="100%">
              <Tooltip label={FTE_TIP} fontSize="xs" whiteSpace="pre-wrap" placement="right">
                <FormLabel fontSize="xs" mb={0.5} cursor="help" textDecoration="underline dotted">
                  Expected FTE (?)
                </FormLabel>
              </Tooltip>
              <Input size="sm" type="number" step="0.1" min="0" max="99"
                value={form.expected_fte} onChange={set('expected_fte')}
                placeholder="e.g. 0.5" />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" isLoading={saving} onClick={save}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Timesheet log modal ───────────────────────────────────────────────────────
function TimesheetModal({ item, itemType, projectId, onClose, onRefresh }) {
  const [sessions, setSessions] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setSessions(item.work_sessions || []);
  }, [item]);

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/work-sessions/${editId}`, editForm);
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      setEditId(null);
      onRefresh();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader fontSize="md" pb={1}>
          🕐 Timesheet — {item.task_title || item.title}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {sessions.length === 0 ? (
            <Text fontSize="12px" color="gray.400" textAlign="center" py={6}>No work sessions logged yet</Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {sessions.map(ws => (
                <Box key={ws.id} p={2} border="1px solid" borderColor="gray.200" borderRadius="md">
                  {editId === ws.id ? (
                    <VStack spacing={2} align="stretch">
                      <HStack spacing={2}>
                        <Box flex={1}>
                          <FormLabel fontSize="xs" mb={0}>Start</FormLabel>
                          <Input size="xs" type="datetime-local"
                            value={editForm.start_time?.replace(' ','T')?.slice(0,16)||''}
                            onChange={e => setEditForm(p => ({ ...p, start_time: e.target.value }))} />
                        </Box>
                        <Box flex={1}>
                          <FormLabel fontSize="xs" mb={0}>End</FormLabel>
                          <Input size="xs" type="datetime-local"
                            value={editForm.end_time?.replace(' ','T')?.slice(0,16)||''}
                            onChange={e => setEditForm(p => ({ ...p, end_time: e.target.value }))} />
                        </Box>
                      </HStack>
                      <Box>
                        <FormLabel fontSize="xs" mb={0}>Delay Reason (if running late)</FormLabel>
                        <Input size="xs" value={editForm.delay_reason||''} placeholder="e.g. unexpected computer failure"
                          onChange={e => setEditForm(p => ({ ...p, delay_reason: e.target.value }))} />
                      </Box>
                      <Box>
                        <FormLabel fontSize="xs" mb={0}>Delay Category</FormLabel>
                        <Select size="xs" value={editForm.delay_category||''}
                          onChange={e => setEditForm(p => ({ ...p, delay_category: e.target.value }))}>
                          <option value="">— None —</option>
                          <option value="technical">Technical</option>
                          <option value="resource">Resource</option>
                          <option value="external">External</option>
                          <option value="scope">Scope Change</option>
                          <option value="other">Other</option>
                        </Select>
                      </Box>
                      <HStack justify="flex-end" spacing={1}>
                        <Button size="xs" variant="ghost" onClick={() => setEditId(null)}>Cancel</Button>
                        <Button size="xs" colorScheme="blue" isLoading={saving} onClick={saveEdit}>Save</Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <Flex align="flex-start" justify="space-between">
                      <Box>
                        <HStack spacing={2} mb={0.5}>
                          <Text fontSize="11px" fontWeight="600">{ws.user_name || 'Unknown'}</Text>
                          <Badge fontSize="8px" colorScheme={ws.status === 'completed' ? 'green' : 'orange'}>
                            {ws.status || 'active'}
                          </Badge>
                          {ws.delay_reason && <Badge fontSize="8px" colorScheme="red">⚠ Delay logged</Badge>}
                        </HStack>
                        <Text fontSize="10px" color="gray.500">
                          {fmtDateTime(ws.start_time)} → {ws.end_time ? fmtDateTime(ws.end_time) : 'ongoing'}
                          {ws.duration_minutes > 0 && ` (${Math.round(ws.duration_minutes / 6) / 10}h)`}
                        </Text>
                        {ws.delay_reason && (
                          <Text fontSize="10px" color="red.600" mt={0.5}>
                            Delay: {ws.delay_reason}
                            {ws.delay_category && <Text as="span" color="red.400"> [{ws.delay_category}]</Text>}
                          </Text>
                        )}
                        {ws.description && <Text fontSize="10px" color="gray.500">{ws.description}</Text>}
                      </Box>
                      <IconButton icon={<FiEdit2 size={11}/>} size="xs" variant="ghost"
                        onClick={() => { setEditId(ws.id); setEditForm({ ...ws }); }} aria-label="Edit" />
                    </Flex>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Main GanttModal ───────────────────────────────────────────────────────────
export default function GanttModal({ projectId, isOpen, onClose }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [showSubs, setShowSubs]   = useState(true);
  const scrollRef  = useRef(null);
  const leftRef    = useRef(null);
  const todayPx    = useRef(0);
  const toast      = useToast();

  // Interaction state
  const [editTarget, setEditTarget]       = useState(null); // { item, itemType }
  const [timesheetTarget, setTimesheetTarget] = useState(null);
  const [ctxMenu, setCtxMenu]             = useState(null); // { x, y, item, itemType, rowId }
  const ctxMenuRef = useRef(null);
  const ctxMenuPos = useMenuPosition(ctxMenuRef, ctxMenu);
  const [depMode, setDepMode]             = useState(false);
  const [depFrom, setDepFrom]             = useState(null);
  const [draggingRow, setDraggingRow]     = useState(null);
  const [dragOverRow, setDragOverRow]     = useState(null);
  const [taskOrder, setTaskOrder]         = useState(null); // null = use data order
  const [resizing, setResizing]           = useState(null);
  const [resizeOverride, setResizeOverride] = useState(null); // { rowId, start, end }
  const [hoverEdge, setHoverEdge]         = useState(null);
  const dayWidth = useRef(20);
  const minDateRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/tasks/gantt/${projectId}`);
      setData(d);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { if (isOpen) { fetchData(); setTaskOrder(null); } }, [isOpen, fetchData]);

  // Build rows directly in render (no async state lag)
  const rows = React.useMemo(() => {
    if (!data) return [];
    let taskList = data.tasks;
    // Apply drag-reorder if active
    if (taskOrder) {
      const byId = {};
      data.tasks.forEach(t => { byId[t.id] = t; });
      taskList = taskOrder.map(id => byId[id]).filter(Boolean);
      // Add any tasks not in taskOrder (shouldn't happen, but safety)
      data.tasks.forEach(t => { if (!taskOrder.includes(t.id)) taskList.push(t); });
    }
    const built = [];
    taskList.forEach(task => {
      built.push({ type:'task', item:task, id:`t-${task.id}`, taskId:task.id });
      if (showSubs) {
        (task.sub_tasks||[]).forEach(st => {
          built.push({ type:'subtask', item:st, id:`s-${st.id}`, taskId:task.id, parentTask:task });
        });
      }
    });
    return built;
  }, [data, showSubs, taskOrder]);

  // Build dep lines
  const depLines = React.useMemo(() => {
    if (!data) return [];
    return (data.ganttDeps || []).map(d => ({
      id: d.id,
      fromId: `${d.from_type==='task'?'t':'s'}-${d.from_id}`,
      toId: `${d.to_type==='task'?'t':'s'}-${d.to_id}`,
    }));
  }, [data]);

  // Scroll to today
  useEffect(() => {
    if (data && scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, todayPx.current - scrollRef.current.clientWidth * 0.3);
      }, 100);
    }
  }, [data]);

  // Close ctx menu on click
  useEffect(() => {
    const h = e => { if (!e.target.closest('[data-gantt-menu]')) setCtxMenu(null); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  if (!data) return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay /><ModalContent>
        <ModalBody><Text p={8} textAlign="center" color="gray.500">{loading ? 'Loading…' : 'No data'}</Text></ModalBody>
      </ModalContent>
    </Modal>
  );

  const { project } = data;
  const today = new Date(); today.setHours(0,0,0,0);

  // Timeline bounds
  const allDates = [today];
  if (project.commissioning_date) allDates.push(safeDate(project.commissioning_date));
  if (project.deadline_date)      allDates.push(safeDate(project.deadline_date));
  data.tasks.forEach(t => {
    ['planned_start','planned_end','computed_start','start_date','computed_deadline','deadline_date','created_at'].forEach(k => {
      if (t[k]) { const d = safeDate(t[k]); if (d && !isNaN(d)) allDates.push(d); }
    });
    (t.sub_tasks||[]).forEach(st => {
      ['planned_start','planned_end','start_date','deadline_date'].forEach(k => {
        if (st[k]) { const d = safeDate(st[k]); if (d && !isNaN(d)) allDates.push(d); }
      });
    });
  });
  const valid = allDates.filter(Boolean);
  const minDate = new Date(Math.min(...valid)); minDate.setDate(minDate.getDate() - 5);
  const maxDate = new Date(Math.max(...valid)); maxDate.setDate(maxDate.getDate() + 14);
  const totalDays = Math.max(30, daysBetween(minDate, maxDate));
  const dw = Math.max(16, Math.min(40, 900 / totalDays));
  dayWidth.current = dw;
  minDateRef.current = minDate;
  const chartW = totalDays * dw;
  const todayOff = daysBetween(minDate, today);
  todayPx.current = todayOff * dw;
  const totalH = rows.length * ROW_H;

  // Build day/week/month markers
  const days = [], months = [];
  const cur = new Date(minDate);
  while (cur <= maxDate) {
    const off = daysBetween(minDate, cur);
    days.push({ off, dow: cur.getDay(), d: cur.getDate() });
    const mk = `${cur.getFullYear()}-${cur.getMonth()}`;
    if (!months.find(m => m.key === mk)) {
      const som = new Date(cur.getFullYear(), cur.getMonth(), 1);
      months.push({ key: mk, label: som.toLocaleDateString('en-GB', { month:'short', year:'numeric' }), off: Math.max(0, daysBetween(minDate, som)) });
    }
    cur.setDate(cur.getDate() + 1);
  }

  // Row index lookup
  const rowByRid = {};
  rows.forEach((r, i) => { rowByRid[r.id] = i; });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleBarContextMenu = (e, row) => {
    e.preventDefault(); e.stopPropagation();
    if (depMode) return;
    setCtxMenu({ x: e.clientX, y: e.clientY, row });
  };

  const handleBarClick = (e, row) => {
    if (!depMode) return;
    e.stopPropagation();
    if (!depFrom) {
      setDepFrom(row);
    } else {
      if (depFrom.id === row.id) { setDepFrom(null); return; }
      // Save dependency
      const fromIsTask = depFrom.type === 'task';
      const toIsTask   = row.type === 'task';
      api.post('/tasks/gantt-deps', {
        project_id: projectId,
        from_type: fromIsTask ? 'task' : 'subtask',
        from_id: depFrom.item.id,
        to_type: toIsTask ? 'task' : 'subtask',
        to_id: row.item.id,
      }).then(() => {
        toast({ title: 'Dependency drawn', status: 'success', duration: 1500 });
        fetchData();
      }).catch(() => toast({ title: 'Failed', status: 'error', duration: 2000 }));
      setDepFrom(null);
    }
  };

  const reorderRow = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || !data) return;
    // Build new task order from current rows
    const taskRows = rows.filter(r => r.type === 'task');
    const newTaskRows = [...taskRows];
    const fromTaskIdx = taskRows.findIndex(r => rows[fromIdx]?.id === r.id);
    const toTaskIdx   = taskRows.findIndex(r => rows[toIdx]?.id === r.id);
    if (fromTaskIdx < 0 || toTaskIdx < 0) return;
    const [moved] = newTaskRows.splice(fromTaskIdx, 1);
    newTaskRows.splice(toTaskIdx, 0, moved);
    setTaskOrder(newTaskRows.map(r => r.item.id));
    try {
      await Promise.all(newTaskRows.map((r, i) =>
        api.put(`/tasks/${r.item.id}`, { sort_order: i * 10 })
      ));
    } catch(e) {}
  };

  const handleResizeMouseDown = (e, row, edge) => {
    e.preventDefault(); e.stopPropagation();
    const bp = getBarProps(row.item, minDate, dw);
    setResizing({ rowId: row.id, edge, startX: e.clientX, origStart: bp.start, origEnd: bp.end, row });
  };

  const handleMouseMove = (e) => {
    if (!resizing) return;
    const dx = e.clientX - resizing.startX;
    const daysDelta = Math.round(dx / dw);
    const newStart = new Date(resizing.origStart);
    const newEnd   = new Date(resizing.origEnd);
    if (resizing.edge === 'left')  newStart.setDate(newStart.getDate() + daysDelta);
    if (resizing.edge === 'right') newEnd.setDate(newEnd.getDate() + daysDelta);
    if (newStart >= newEnd) return;
    setResizeOverride({ rowId: resizing.rowId, start: toYMD(newStart), end: toYMD(newEnd) });
  };

  const handleMouseUp = async () => {
    if (!resizing) return;
    const override = resizeOverride;
    setResizing(null);
    setResizeOverride(null);
    if (!override) return;
    const row = resizing.row;
    const item = row.item;
    const isTask = row.type === 'task';
    const payload = {
      planned_start: override.start, planned_end: override.end,
      start_date: override.start, deadline_date: override.end,
    };
    try {
      if (isTask) await api.put(`/tasks/${item.id}`, payload);
      else await api.put(`/sub-tasks/${item.id}`, payload);
      fetchData();
    } catch(e) {}
  };

  const deleteDep = async (depId) => {
    await api.delete(`/tasks/gantt-deps/${depId}`).catch(() => {});
    fetchData();
  };

  // ── Bar component ─────────────────────────────────────────────────────────
  const renderBar = (row, i) => {
    const { item, type, id } = row;
    const isSub = type === 'subtask';
    // Use live resize override if dragging this bar's edge
    const override = resizeOverride?.rowId === id ? resizeOverride : null;
    const effectiveItem = override
      ? { ...item, start_date: override.start, deadline_date: override.end }
      : item;
    const { left, width, start, end } = getBarProps(effectiveItem, minDate, dw);
    const status = barStatus(effectiveItem, today);
    const pct = item.latest_percentage || item.completion_percentage || 0;
    const isHoverEdgeL = hoverEdge?.rowId === id && hoverEdge?.edge === 'left';
    const isHoverEdgeR = hoverEdge?.rowId === id && hoverEdge?.edge === 'right';
    const isResizingThis = resizing?.rowId === id;
    const isDepFrom = depFrom?.id === id;

    // Planned vs live overrun
    const plannedStart = safeDate(item.planned_start);
    const plannedEnd   = safeDate(item.planned_end);
    const showOverrun = plannedEnd && end > plannedEnd;
    const plannedLeft  = plannedStart ? daysBetween(minDate, plannedStart) * dw : null;
    const plannedWidth = (plannedStart && plannedEnd) ? Math.max(dw, daysBetween(plannedStart, plannedEnd) * dw) : null;

    const label = item.task_title || item.title || '—';
    const tooltip = `${label}
${fmtDate(start)} → ${fmtDate(end)}
${pct}%${item.expected_fte ? ` · ${item.expected_fte} FTE` : ''}
Status: ${status.label}`;

    return (
      <Box key={`br-${id}`} h={`${ROW_H}px`} position="relative"
        borderBottom="1px solid" borderColor="gray.100"
        bg={i%2===0?(isSub?'purple.50':'white'):(isSub?'purple.100':'gray.50')}
        onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>

        {/* Planned baseline ghost (if overrun) */}
        {plannedLeft !== null && plannedWidth !== null && showOverrun && (
          <Box position="absolute" top={`${BAR_T}px`} left={`${plannedLeft}px`}
            h={`${BAR_H}px`} w={`${plannedWidth}px`} borderRadius="4px" pointerEvents="none"
            border="2px dashed #E53E3E" opacity={0.4} zIndex={0} />
        )}

        <Tooltip label={tooltip} fontSize="xs" whiteSpace="pre-wrap" placement="top">
          <Box position="absolute" top={`${BAR_T + (isSub?4:0)}px`}
            left={`${left}px`}
            h={`${BAR_H - (isSub?8:0)}px`} w={`${width}px`}
            borderRadius="4px"
            bg={status.bg}
            border={`2px solid ${isDepFrom ? '#805AD5' : status.border}`}
            boxShadow={isDepFrom ? '0 0 0 3px #805AD544' : undefined}
            overflow="hidden" cursor={depMode ? 'crosshair' : 'default'}
            transition="box-shadow 0.15s"
            onContextMenu={e => handleBarContextMenu(e, row)}
            onClick={e => handleBarClick(e, row)}>

            {/* Progress fill */}
            <Box h="100%" w={`${pct}%`} bg={status.border} opacity={0.18} position="absolute" left={0} top={0} pointerEvents="none" />

            {/* Label */}
            <Flex h="100%" align="center" px={1.5} position="relative" zIndex={1}>
              <Text fontSize="9px" fontWeight="700" color={status.text} noOfLines={1}>{pct>0?`${pct}% `:''}  {label}</Text>
              {item.expected_fte && (
                <Tooltip label={FTE_TIP} fontSize="xs" whiteSpace="pre-wrap">
                  <Text fontSize="8px" color={status.text} opacity={0.7} ml={1} flexShrink={0}>{item.expected_fte}FTE</Text>
                </Tooltip>
              )}
            </Flex>

            {/* Left resize handle */}
            <Box position="absolute" left={0} top={0} bottom={0} w={`${RESIZE_ZONE}px`}
              cursor="w-resize" zIndex={10}
              opacity={isHoverEdgeL||isResizingThis?1:0}
              bg="blackAlpha.100"
              onMouseEnter={() => !depMode && setHoverEdge({ rowId:id, edge:'left' })}
              onMouseLeave={() => setHoverEdge(null)}
              onMouseDown={e => handleResizeMouseDown(e, row, 'left')} />
            {/* Right resize handle */}
            <Box position="absolute" right={0} top={0} bottom={0} w={`${RESIZE_ZONE}px`}
              cursor="e-resize" zIndex={10}
              opacity={isHoverEdgeR||isResizingThis?1:0}
              bg="blackAlpha.100"
              onMouseEnter={() => !depMode && setHoverEdge({ rowId:id, edge:'right' })}
              onMouseLeave={() => setHoverEdge(null)}
              onMouseDown={e => handleResizeMouseDown(e, row, 'right')} />
          </Box>
        </Tooltip>
      </Box>
    );
  };

  // ── Dependency SVG lines ──────────────────────────────────────────────────
  const svgLines = [];
  depLines.forEach(dep => {
    const fromIdx = rowByRid[dep.fromId];
    const toIdx   = rowByRid[dep.toId];
    if (fromIdx === undefined || toIdx === undefined) return;
    const fromRow = rows[fromIdx];
    const toRow   = rows[toIdx];
    if (!fromRow || !toRow) return;
    const fromBar = getBarProps(fromRow.item, minDate, dw);
    const toBar   = getBarProps(toRow.item, minDate, dw);
    const x1 = fromBar.left + fromBar.width;
    const y1 = fromIdx * ROW_H + BAR_T + BAR_H / 2;
    const x2 = toBar.left;
    const y2 = toIdx * ROW_H + BAR_T + BAR_H / 2;
    const cx = (x1 + x2) / 2;
    const path = `M${x1},${y1} C${cx},${y1} ${cx},${y2} ${x2},${y2}`;
    svgLines.push({ id: dep.id, path, x2, y2 });
  });

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent maxH="96vh" display="flex" flexDirection="column">
        <ModalHeader fontSize="md" pb={1} flexShrink={0}>
          <Flex justify="space-between" align="center" pr={10}>
            <Box>
              <Text>{project.project_name || 'Untitled'}</Text>
              <Text fontSize="11px" color="gray.500" fontWeight="400">
                {project.company_name || ''}
                {project.commissioning_date && ` · Started ${fmtDate(project.commissioning_date)}`}
                {project.deadline_date      && ` · Deadline ${fmtDate(project.deadline_date)}`}
              </Text>
            </Box>
            <HStack spacing={3}>
              {depMode && (
                <Badge colorScheme="purple" fontSize="10px" px={2} py={1}>
                  {depFrom ? `Click target for "${depFrom.item.task_title||depFrom.item.title}"` : '🔗 Click a bar to start dependency'}
                </Badge>
              )}
              <HStack spacing={1} fontSize="11px">
                <Text color="gray.500">Subtasks</Text>
                <Switch size="sm" isChecked={showSubs} onChange={e => setShowSubs(e.target.checked)} colorScheme="purple" />
              </HStack>
              {depMode ? (
                <Button size="xs" colorScheme="purple" variant="solid" onClick={() => { setDepMode(false); setDepFrom(null); }}>
                  Exit Link Mode
                </Button>
              ) : null}
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0} overflow="hidden" flex={1} display="flex" flexDirection="column"
          onMouseUp={handleMouseUp}>
          <Flex flex={1} overflow="hidden">

            {/* Left label panel */}
            <Box w={`${LEFT_W}px`} minW={`${LEFT_W}px`} borderRight="2px solid" borderColor="gray.300"
              flexShrink={0} bg="white" display="flex" flexDirection="column" overflow="hidden" ref={leftRef}>
              <Box h={`${HEAD_H}px`} bg="gray.700" px={3} display="flex" alignItems="center"
                borderBottom="1px solid" borderColor="gray.600" flexShrink={0}>
                <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="white">Task</Text>
              </Box>
              <Box overflowY="auto" flex={1}>
                {rows.map((row, i) => {
                  const { item, type, id } = row;
                  const isSub = type === 'subtask';
                  const status = barStatus(item, today);
                  const isBlocked = (item.dependencies||[]).some(d=>d.dependency_type==='blocks'&&!d.depends_on_complete);
                  return (
                    <Box key={`ll-${id}`} h={`${ROW_H}px`} px={2} pl={isSub?7:2}
                      display="flex" alignItems="center"
                      borderBottom="1px solid" borderColor="gray.100"
                      bg={i%2===0?(isSub?'purple.50':'white'):(isSub?'purple.100':'gray.50')}
                      borderLeft={`3px solid ${status.border}`}
                      draggable
                      onDragStart={() => setDraggingRow(i)}
                      onDragOver={e => { e.preventDefault(); setDragOverRow(i); }}
                      onDrop={() => { reorderRow(draggingRow, i); setDraggingRow(null); setDragOverRow(null); }}
                      cursor="grab"
                      boxShadow={dragOverRow === i ? 'inset 0 2px 0 #3182CE' : undefined}>
                      <Flex direction="column" flex={1} overflow="hidden">
                        <HStack spacing={1}>
                          {isSub && <Text fontSize="9px" color="purple.300">└</Text>}
                          <Text fontSize={isSub?'10px':'11px'} fontWeight="600" noOfLines={1} flex={1}
                            color={isSub?'purple.800':'gray.800'}>
                            {item.task_title || item.title || '—'}
                          </Text>
                          {(item.is_complete||item.is_completed) && <Badge fontSize="7px" colorScheme="green">✓</Badge>}
                          {!!item.is_held && <Badge fontSize="7px" colorScheme="red">Held</Badge>}
                          {isBlocked && <Badge fontSize="7px" colorScheme="red" variant="outline">Blocked</Badge>}
                        </HStack>
                        <HStack spacing={2} fontSize="9px" color="gray.400" pl={isSub?3:0}>
                          <Text>{item.latest_percentage||item.completion_percentage||0}%</Text>
                          {item.expected_fte && (
                            <Tooltip label={FTE_TIP} fontSize="xs" whiteSpace="pre-wrap">
                              <Text color="blue.400" cursor="help">{item.expected_fte}FTE</Text>
                            </Tooltip>
                          )}
                          {!isSub && <Text>{(item.total_hours||0).toFixed(1)}h</Text>}
                          {isSub && item.projected_hours > 0 && <Text>{item.projected_hours}h</Text>}
                        </HStack>
                      </Flex>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Right chart panel */}
            <Box flex={1} overflow="auto" ref={scrollRef} position="relative">
              <Box position="relative" minW={`${chartW}px`} w={`${chartW}px`}
                userSelect={resizing ? 'none' : 'auto'}>

                {/* Sticky header */}
                <Box h={`${HEAD_H}px`} bg="gray.700" position="sticky" top={0} zIndex={5}
                  borderBottom="1px solid" borderColor="gray.600">
                  {/* Month labels */}
                  {months.map(m => (
                    <Box key={m.key} position="absolute" left={`${m.off*dw}px`} h="100%"
                      display="flex" alignItems="center" borderLeft="1px solid" borderColor="gray.500" px={2}>
                      <Text fontSize="10px" color="white" fontWeight="600" whiteSpace="nowrap">{m.label}</Text>
                    </Box>
                  ))}
                  {/* Day numbers in header */}
                  {days.map(d => (
                    <Box key={d.off} position="absolute" left={`${d.off*dw}px`} bottom="2px"
                      w={`${dw}px`} textAlign="center">
                      <Text fontSize="7px" color={d.dow===0||d.dow===6?'red.300':'gray.400'}>{d.d}</Text>
                    </Box>
                  ))}
                  {/* Today line */}
                  <Box position="absolute" left={`${todayOff*dw}px`} top={0} bottom={0} w="2px" bg="red.300" zIndex={2} />
                </Box>

                {/* Background gridlines */}
                <Box position="absolute" top={`${HEAD_H}px`} left={0} right={0} bottom={0} pointerEvents="none" zIndex={0}>
                  {/* Day gridlines — light grey */}
                  {days.map(d => (
                    <Box key={d.off} position="absolute" left={`${d.off*dw}px`} top={0} bottom={0}
                      w="1px" bg={d.dow===0||d.dow===6?'gray.200':'gray.100'} />
                  ))}
                  {/* Today */}
                  <Box position="absolute" left={`${todayOff*dw}px`} top={0} bottom={0} w="2px" bg="red.400" opacity={0.7} />
                  {/* Project deadline */}
                  {project.deadline_date && (() => {
                    const off = daysBetween(minDate, safeDate(project.deadline_date)) * dw;
                    return <Box position="absolute" left={`${off}px`} top={0} bottom={0} w="0" borderLeft="2px dashed" borderColor="purple.400" opacity={0.6} />;
                  })()}
                </Box>

                {/* Bars */}
                <Box position="relative" zIndex={1} mt={`${HEAD_H}px`}>
                  {rows.map((row, i) => renderBar(row, i))}
                </Box>

                {/* Dependency lines SVG */}
                <svg style={{ position:'absolute', top:`${HEAD_H}px`, left:0, width:chartW, height:totalH, pointerEvents:'none', zIndex:4, overflow:'visible' }}>
                  <defs>
                    <marker id="depArrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                      <path d="M0,0 L7,3.5 L0,7 Z" fill="#A0AEC0" />
                    </marker>
                  </defs>
                  {svgLines.map(line => (
                    <g key={line.id}>
                      <path d={line.path} fill="none" stroke="#A0AEC0" strokeWidth="2"
                        strokeDasharray="none" markerEnd="url(#depArrow)" opacity={0.8} />
                      <circle cx={line.x2} cy={line.y2} r={3} fill="#A0AEC0" opacity={0.9} />
                    </g>
                  ))}
                  {/* Live dependency drawing line */}
                  {depFrom && (() => {
                    const fromIdx = rowByRid[depFrom.id];
                    if (fromIdx === undefined) return null;
                    const fb = getBarProps(depFrom.item, minDate, dw);
                    const x1 = fb.left + fb.width;
                    const y1 = fromIdx * ROW_H + BAR_T + BAR_H / 2;
                    return (
                      <circle cx={x1} cy={y1} r={6} fill="none" stroke="#805AD5" strokeWidth="2" strokeDasharray="4,2">
                        <animate attributeName="r" values="5;8;5" dur="1.5s" repeatCount="indefinite" />
                      </circle>
                    );
                  })()}
                </svg>
              </Box>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter flexShrink={0} borderTop="1px solid" borderColor="gray.100" py={2}>
          <HStack spacing={3} mr="auto" fontSize="9px" flexWrap="wrap">
            {[
              ['#C6F6D5','#38A169','Complete'],['#FEFCBF','#D69E2E','In Progress'],
              ['#BEE3F8','#3182CE','Not Started'],['#FED7D7','#E53E3E','Overdue'],
            ].map(([bg,bd,lbl]) => (
              <Flex key={lbl} align="center" gap={1}>
                <Box w="10px" h="10px" bg={bg} border={`2px solid ${bd}`} borderRadius="sm" />
                <Text>{lbl}</Text>
              </Flex>
            ))}
            <Flex align="center" gap={1}><Box w="12px" h="2px" bg="red.400"/><Text>Today</Text></Flex>
            <Flex align="center" gap={1}><Box w="12px" h="0" borderBottom="2px dashed" borderColor="red.400" opacity={0.4}/><Text>Planned (overrun)</Text></Flex>
            <Flex align="center" gap={1}><Box w="16px" h="0" borderTop="2px solid" borderColor="gray.400"/><Text>Dependency</Text></Flex>
          </HStack>
          <Button size="xs" leftIcon={<FiLink size={11}/>} colorScheme="purple" variant={depMode?'solid':'outline'}
            mr={2} onClick={() => { setDepMode(!depMode); setDepFrom(null); }}>
            {depMode ? 'Exit Link Mode' : 'Draw Dependencies'}
          </Button>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Context menu */}
    {ctxMenu && (
      <Box data-gantt-menu ref={ctxMenuRef}
        position="fixed" left={ctxMenuPos.left} top={ctxMenuPos.top}
        bg="white" border="1px solid" borderColor="gray.200" borderRadius="md"
        boxShadow="lg" zIndex={9999} py={1} minW="180px"
        maxH={ctxMenuPos.maxHeight} maxW={ctxMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
        visibility={ctxMenuPos.ready ? 'visible' : 'hidden'}>
        {[
          { icon: FiEdit2, label: 'Edit dates & FTE', action: () => { setEditTarget({ item:ctxMenu.row.item, itemType:ctxMenu.row.type }); setCtxMenu(null); } },
          { icon: FiClock, label: 'Timesheet log', action: () => { setTimesheetTarget({ item:ctxMenu.row.item, itemType:ctxMenu.row.type }); setCtxMenu(null); } },
          { icon: FiLink,  label: 'Set dependency', action: () => { setDepMode(true); setDepFrom(ctxMenu.row); setCtxMenu(null); } },
        ].map(({ icon: Icon, label, action }) => (
          <Box key={label} px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg:'blue.50' }}
            onClick={action}>
            <HStack spacing={2}><Icon size={12}/><Text>{label}</Text></HStack>
          </Box>
        ))}
        {/* Show dep lines for this element */}
        {depLines.filter(d => d.fromId === ctxMenu.row.id || d.toId === ctxMenu.row.id).length > 0 && (
          <>
            <Box h="1px" bg="gray.100" my={1}/>
            {depLines.filter(d => d.fromId === ctxMenu.row.id || d.toId === ctxMenu.row.id).map(dep => (
              <Box key={dep.id} px={3} py={1.5} fontSize="11px" cursor="pointer"
                color="red.500" _hover={{ bg:'red.50' }}
                onClick={() => { deleteDep(dep.id); setCtxMenu(null); }}>
                ✕ Remove dependency link
              </Box>
            ))}
          </>
        )}
      </Box>
    )}

    {/* Edit modal */}
    {editTarget && (
      <EditModal item={editTarget.item} itemType={editTarget.itemType}
        projectId={projectId}
        onSave={() => { setEditTarget(null); fetchData(); }}
        onClose={() => setEditTarget(null)} />
    )}

    {/* Timesheet modal */}
    {timesheetTarget && (
      <TimesheetModal item={timesheetTarget.item} itemType={timesheetTarget.itemType}
        projectId={projectId}
        onClose={() => setTimesheetTarget(null)}
        onRefresh={fetchData} />
    )}
    </>
  );
}
